import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getPost } from "../redux/actions";

// Dedup by id, preserving order — mirrors the helper in src/pages/Post/index.js.
const uniqueById = (list) => {
  const map = new Map();
  for (const item of list) {
    if (item && item.id !== undefined && item.id !== null) {
      map.set(item.id, item);
    }
  }
  return Array.from(map.values());
};

// Loads a post's comments with pagination, keeping the list in LOCAL state
// seeded from the thunk's return value (NOT the shared state.groups.getPost
// slot) — same pattern PostPage uses, so the sheet and any mounted page never
// clobber each other.
export const usePostComments = (postId, aroundCommentId = null) => {
  const dispatch = useDispatch();
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(null);
  const [page, setPage] = useState(1);
  const [isInfiniteDisabled, setInfiniteDisabled] = useState(false);
  const [hasInitialLoaded, setHasInitialLoaded] = useState(false);
  const [error, setError] = useState(false);
  // Which top-level comment holds the linked one, per the API — so only that
  // thread opens, instead of every thread on the page.
  const [anchorCommentId, setAnchorCommentId] = useState(null);
  const inFlightRef = useRef(false);
  const lastPostIdRef = useRef(null);
  // Read at load time rather than as an effect dependency: the focused comment
  // is cleared once it has been scrolled to, and that must not refetch the list.
  const aroundCommentIdRef = useRef(aroundCommentId);
  aroundCommentIdRef.current = aroundCommentId;
  // The page size the API actually used — larger than the default when it
  // stretched page one to reach a deep-linked comment. Later pages must ask for
  // the same size or they would re-serve rows already on screen.
  const perPageRef = useRef(null);
  // One page at a time: true while a later page is on its way. Ionic's infinite
  // scroll fires again while its trigger is still in view, and completing it
  // before the page arrived let it fire dozens of times a second — comment
  // pages 2 to 47 of one post requested at once, three times over, spending
  // the member's API allowance so Home and the feed failed (07.10.2026).
  const pageLoadingRef = useRef(false);
  const pendingScrollRef = useRef(null);
  const finishScroll = () => {
    pendingScrollRef.current?.target?.complete?.();
    pendingScrollRef.current = null;
  };

  // Reset SYNCHRONOUSLY when the post changes (React "adjust state while
  // rendering" pattern). Without this, reopening the sheet for a different
  // post paints the PREVIOUS post's comments — and its stale count drives the
  // sheet height — for a frame before the effect below clears them.
  const [trackedPostId, setTrackedPostId] = useState(postId);
  if (postId !== trackedPostId) {
    setTrackedPostId(postId);
    // Allow the load effect below to run again — otherwise closing and
    // reopening the sheet for the SAME post clears hasInitialLoaded here but
    // the lastPostIdRef guard blocks a re-fetch, leaving it stuck on skeletons.
    lastPostIdRef.current = null;
    inFlightRef.current = false;
    perPageRef.current = null;
    pageLoadingRef.current = false;
    finishScroll();
    setAnchorCommentId(null);
    setComments([]);
    setCount(null);
    setPage(1);
    setInfiniteDisabled(false);
    setHasInitialLoaded(false);
    setError(false);
  }

  // Initial load (once per postId)
  useEffect(() => {
    if (!postId) return;
    if (lastPostIdRef.current === postId) return;
    if (inFlightRef.current) return;
    lastPostIdRef.current = postId;
    inFlightRef.current = true;
    setPage(1);
    setComments([]);
    setInfiniteDisabled(false);
    setHasInitialLoaded(false);
    setError(false);
    perPageRef.current = null;
    dispatch(
      getPost({
        postId,
        page: 1,
        aroundCommentId: aroundCommentIdRef.current || undefined,
      })
    )
      .then((data) => {
        const batch = data.payload?.comments?.data || [];
        perPageRef.current = data.payload?.comments?.per_page ?? null;
        setAnchorCommentId(data.payload?.anchor_comment_id ?? null);
        const total =
          data.payload?.post_group?.comments_count ??
          data.payload?.comments?.total ??
          null;
        setCount(total);
        setComments(uniqueById(batch));
        if (batch.length === 0) setInfiniteDisabled(true);
        setHasInitialLoaded(true);
      })
      .catch(() => setError(true))
      .finally(() => {
        inFlightRef.current = false;
      });
  }, [postId, dispatch]);

  // Subsequent pages
  useEffect(() => {
    if (!hasInitialLoaded || page <= 1) return;
    pageLoadingRef.current = true;
    const pageSize = perPageRef.current;
    dispatch(
      getPost({ postId, page, perPage: pageSize || undefined })
    )
      .then((data) => {
        const batch = data.payload?.comments?.data || [];
        if (batch.length === 0) {
          setInfiniteDisabled(true);
          return;
        }
        setComments((prev) => uniqueById([...prev, ...batch]));
        // A short page is the last one: no need to ask for an empty one.
        if (pageSize && batch.length < pageSize) setInfiniteDisabled(true);
      })
      .finally(() => {
        pageLoadingRef.current = false;
        finishScroll();
      });
  }, [page, dispatch, postId, hasInitialLoaded]);

  const loadMore = useCallback(
    (event) => {
      if (isInfiniteDisabled || !hasInitialLoaded || pageLoadingRef.current) {
        event?.target?.complete?.();
        return;
      }
      // Completed when the page arrives, so Ionic cannot fire again meanwhile.
      pageLoadingRef.current = true;
      pendingScrollRef.current = event;
      setPage((p) => p + 1);
    },
    [isInfiniteDisabled, hasInitialLoaded]
  );

  return {
    comments,
    count,
    anchorCommentId,
    setComments,
    loadMore,
    isInfiniteDisabled,
    hasInitialLoaded,
    error,
  };
};

export default usePostComments;
